/**
 * useSessionProvider: composition-root lifecycle hook that owns the single
 * BFF-backed session provider (`RealSessionProvider`) and makes its
 * construction / disposal StrictMode-safe (spec 023, D-106 / FR-024; the
 * dual-audience collapse is 032, RD-111 / T015).
 *
 * ## The collapse (RD-111)
 *
 * The dual-audience composite (`DualAudienceSessionProvider`) existed only to
 * pair the admin BFF source with a demo MEMBER source while the member arm of
 * `session.me` was deferred. Now that `session.me` returns the role for BOTH
 * audiences, the composite is redundant: this hook returns a single
 * `RealSessionProvider` that reports `admin`, `member`, or `anonymous` from one
 * server round-trip. The demo source is unwired (its files retire in T035).
 *
 * Extracted from `main.tsx`'s `Composition` so the StrictMode revive-on-remount
 * lifecycle is unit-testable in isolation (a `renderHook` inside `<StrictMode>`
 * with an async-resolving provider), without standing up the whole router /
 * ports / `#root` tree. The hook returns a STABLE, NON-NULL concrete
 * `RealSessionProvider` that backs the `ports.session` slot (typed
 * `SessionPort`). The `ports.demoSessionControls` slot is no longer fed from
 * here — the real provider has no demo controls, so that slot is `undefined` in
 * production (its documented post-cutover state; consumers tolerate the
 * absence).
 *
 * ## The StrictMode dispose-drop bug this hook fixes
 *
 * `RealSessionProvider` seeds `{ kind: 'loading' }` and learns identity from an
 * async `session.me` round-trip fired in its constructor (`refresh()`), which on
 * resolution transitions `loading -> anonymous|member|admin`. `refresh()`
 * carries a `if (this.#disposed) return;` guard so a late resolution after
 * teardown is dropped.
 *
 * Under React StrictMode in dev (the app is wrapped in `<StrictMode>`) the mount
 * lifecycle is: mount -> run effect setup -> SYNTHETIC UNMOUNT (cleanup runs
 * `provider.dispose()`) -> remount -> run effect setup again. The naive
 * implementation held the provider in a bare `useState(makeProvider)` and the
 * cleanup disposed it. Because `useState` preserves the SAME instance across the
 * synthetic remount, the disposed provider is reused after remount. The
 * constructor's in-flight `refresh()` then resolves `session.me`, hits the
 * `#disposed` guard, and the `loading -> resolved` transition is PERMANENTLY
 * dropped: every session-dependent surface sticks on the loading placeholder
 * forever. This manifests only in dev (production builds do not double-mount),
 * but it breaks `pnpm dev` and the Playwright dev-project E2E, and it is a
 * genuine lifecycle-correctness bug regardless.
 *
 * ## The fix: revive-on-remount
 *
 * The dispose effect detects re-entry holding a disposed provider (the
 * StrictMode remount) and replaces it with a fresh one via `setSessionProvider`.
 * The fresh provider's `refresh()` is NOT disposed, so the `loading -> resolved`
 * transition lands this time. The revive branch returns NO cleanup so the swap
 * does not cascade into a dispose-then-revive loop (see the `useEffect` body for
 * the full loop trace).
 *
 * ### Why a fresh provider and not a "re-arm" of the disposed one
 *
 * `dispose()` is intentionally terminal and idempotent (it clears listeners, and
 * the provider has no cancel handle for the in-flight `session.me`, so its
 * `#disposed` guard is the sole cancel mechanism). Reviving a disposed instance
 * in place would mean un-setting `#disposed` and re-subscribing, widening the
 * mutable surface. Constructing a fresh provider reuses the existing,
 * well-tested construction path verbatim and keeps disposal terminal. The
 * disposed instance is unreferenced after the swap and is collected; its dropped
 * `refresh()` resolves into the `#disposed` guard and is a harmless no-op.
 *
 * ## Identity stability (load-bearing for useSyncExternalStore)
 *
 * `Composition` wraps the `ports` object in `useMemo` keyed on `sessionProvider`,
 * and `useSession()` feeds `getSnapshot = () => port.state` to
 * `useSyncExternalStore`, which compares by reference identity. The returned
 * provider reference therefore MUST stay stable across ordinary re-renders and
 * change ONLY on a genuine re-creation. It does: the reference comes from
 * `useState`, so it is stable across re-renders, and it moves exactly once, on
 * the StrictMode revive (a real re-creation that SHOULD re-key the memo and
 * re-subscribe the store to the live provider). In production, where there is no
 * synthetic remount, it never moves.
 *
 * ## Genuine-unmount / test-cleanup disposal is preserved
 *
 * On a real unmount (and on test cleanup via `renderAppWithPorts`) the effect
 * cleanup still runs `dispose()` on whatever provider is current, with no
 * remount to revive it. The disposal semantics the tests rely on are unchanged;
 * only the dev-mode synthetic-remount path gains the revive.
 *
 * ## `.ts` (no JSX)
 *
 * This is a plain hook module with no JSX, so it carries the `.ts` extension
 * (unlike the adapter class, which uses `.tsx` to match its task descriptor's
 * stated path). It lives under `session/` alongside the provider it manages.
 */

import { useCallback, useEffect, useState } from 'react';
import type { WebTrpcClient } from '../trpc/client.js';
import { createWebTrpcClient } from '../trpc/client.js';
import { RealSessionProvider } from './RealSessionProvider.js';
import type { SessionPort } from './SessionPort.js';
import {
  openSessionRevocationChannel,
  subscribeSessionInvalidation,
} from './sessionInvalidation.js';

/**
 * The structural shape of the session provider this hook owns: a `SessionPort`
 * plus the lifecycle seams the StrictMode-revive logic reads (`dispose()`,
 * `isDisposed`). `RealSessionProvider` satisfies it in production; the test
 * harness and unit tests pass lightweight structural stubs (e.g. an
 * anonymous-immediate provider, an async-resolving one) that also satisfy it
 * without constructing the concrete adapter. Declared structurally (not as the
 * concrete class) so those stubs are assignable — the same decoupling the
 * pre-collapse hook had via its `DualAudienceSessionProvider`-typed seam, now
 * named for the single source (RD-111).
 */
export type SessionProviderHandle = SessionPort & {
  dispose(): void;
  refresh?(): Promise<void>;
  invalidate?(): void;
  revalidate?(): Promise<void>;
  readonly isDisposed: boolean;
};

/**
 * Hook dependencies. Both are injectable so the colocated test substitutes
 * lightweight async-resolving stubs for the real tRPC seam (no network, no
 * `window.location`, deterministic timing) while exercising the EXACT lifecycle
 * wiring `main.tsx` ships. Production omits both and gets the real provider.
 *
 * - `createProvider`: the construction body itself, overridable WHOLESALE in the
 *   test so it can hand the hook a faithful structural provider stub (an
 *   anonymous-immediate or async-resolving `SessionProviderHandle`) without
 *   constructing the concrete adapter. When provided, it is the sole source of
 *   new providers, including each StrictMode revive, so the test counts revivals
 *   by counting factory calls.
 * - `trpc`: the typed web tRPC client the provider reads `session.me` from,
 *   narrowed to the `session` namespace the provider actually consumes. Used
 *   only by the default construction path (when `createProvider` is omitted);
 *   defaults to a fresh production client. A test that wants the real adapter
 *   but a stub transport injects this instead of `createProvider`.
 *
 * Both fields are read only on the FIRST mount (the `useState` lazy initialiser)
 * and on each StrictMode revive; a `useState` initialiser does not re-run on
 * updates, so changing either between renders after mount has no effect. A test
 * that needs a different composition remounts the hook rather than mutating these.
 */
export interface UseSessionProviderDeps {
  readonly createProvider?: () => SessionProviderHandle;
  readonly trpc?: Pick<WebTrpcClient, 'session'>;
}

/**
 * Build the production provider: a single `RealSessionProvider` over the typed
 * tRPC client. Its constructor fires the floating `session.me` `refresh()`, so
 * identity resolves asynchronously (the `loading` seed until then). Mirrors the
 * construction that lived inline in `main.tsx`'s `Composition` before the
 * extraction, minus the demo arm and the composite wrapper (RD-111).
 */
function createDefaultProvider(trpc: Pick<WebTrpcClient, 'session'>): SessionProviderHandle {
  return new RealSessionProvider({ trpc });
}

/**
 * Returns the live BFF session provider, StrictMode-safe. Stable across ordinary
 * re-renders; re-created exactly once on a StrictMode dev-mode synthetic remount
 * (so its `loading -> resolved` transition is never dropped); disposed on
 * genuine unmount and test cleanup.
 *
 * The default-deps path constructs the production provider. Tests pass
 * `createProvider` to wire a faithful async-resolving stub and assert the revive.
 */
export function useSessionProvider(deps: UseSessionProviderDeps = {}): SessionProviderHandle {
  const { createProvider, trpc } = deps;

  // Stable factory across re-renders so the dispose effect's dependency list is
  // honest: the effect depends on `makeSessionProvider`, and an unstable
  // identity would re-run it every render, disposing and re-creating the
  // provider on each pass. In production both `createProvider` and `trpc` are
  // `undefined` and stable, so this callback is created once; the default
  // production client is built lazily INSIDE the factory (only when a provider
  // is actually constructed), never captured in the dep array.
  // react-compiler-exception: effect-dependency stability (manual identity pin is load-bearing for the dispose-effect dep array; re-running it would dispose+recreate the live provider every render)
  const makeSessionProvider = useCallback((): SessionProviderHandle => {
    if (createProvider !== undefined) {
      return createProvider();
    }
    return createDefaultProvider(trpc ?? createWebTrpcClient());
  }, [createProvider, trpc]);

  // Lazy initialiser: construct exactly one provider on first mount. Its
  // identity is stable for the component's lifetime (R6/R47): the state machine
  // lives in the provider's own `#state`, not React state, and consumers
  // subscribe through `port.subscribe(...)` via `useSyncExternalStore`.
  const [sessionProvider, setSessionProvider] =
    useState<SessionProviderHandle>(makeSessionProvider);

  useEffect(() => {
    // StrictMode (dev) runs this effect's cleanup (disposing the provider) and
    // then re-runs setup holding the SAME `useState` instance. A disposed
    // provider can never resolve its in-flight `session.me` (the `#disposed`
    // guard drops the `loading -> resolved` transition), so when setup
    // re-enters holding a disposed provider, swap in a fresh one whose
    // `refresh()` is NOT disposed.
    //
    // CRUCIAL: the revive branch returns NO cleanup. If it returned a cleanup
    // capturing the fresh provider, the `setSessionProvider` re-render would run
    // THAT cleanup (disposing the fresh provider) before the next setup adopts
    // it, the next setup would then see the just-adopted provider already
    // disposed and revive AGAIN, and the dispose-then-revive cascade would loop
    // forever (an out-of-memory crash, not a hang). By returning early with no
    // cleanup, disposal of the fresh provider is owned by the steady-state
    // effect run that adopts it (the run below the `if`), so each provider is
    // disposed exactly once.
    //
    // Loop trace (StrictMode initial mount):
    //   1. setup#1 (provider P1, not disposed) -> cleanup captures P1.
    //   2. synthetic unmount: cleanup#1 -> P1.dispose(). P1 disposed.
    //   3. setup#2: `sessionProvider` is still P1, disposed -> build P2,
    //      setSessionProvider(P2), RETURN with no cleanup.
    //   4. re-render: `sessionProvider` is P2. Dep changed, so React runs
    //      effect#2's cleanup (none) then setup#3.
    //   5. setup#3: P2 not disposed -> cleanup captures P2. Steady state.
    // A genuine unmount later runs setup#3's cleanup -> P2.dispose(), with NO
    // following setup, so disposal stays terminal. In production (no synthetic
    // remount) the revive branch is never taken: P1 is built once and disposed
    // once.
    if (sessionProvider.isDisposed) {
      setSessionProvider(makeSessionProvider());
      return;
    }
    return () => {
      sessionProvider.dispose();
    };
  }, [sessionProvider, makeSessionProvider]);

  useEffect(() => {
    const refresh = (): void => {
      void sessionProvider.refresh?.();
    };
    const visible = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    const unsubscribeInvalidation = subscribeSessionInvalidation(() => {
      sessionProvider.invalidate?.();
    });
    const channel = openSessionRevocationChannel();
    if (channel)
      channel.onmessage = (event: MessageEvent<unknown>) => {
        if (event.data === 'revoked')
          void (sessionProvider.revalidate ?? sessionProvider.refresh)?.call(sessionProvider);
      };
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', visible);
    return () => {
      unsubscribeInvalidation();
      channel?.close();
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [sessionProvider]);

  return sessionProvider;
}
